import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Hero from '@/components/Hero';
import FeaturedDisplays from '@/components/FeaturedDisplays';
import PopularSequences from '@/components/PopularSequences';
import HowItWorks from '@/components/HowItWorks';
import CTASection from '@/components/CTASection';
import Footer from '@/components/Footer';
import { 
  SchemaMarkup, 
  createOrganizationSchema, 
  createSearchActionSchema 
} from '@/components/seo/SchemaMarkup';

const Index = () => {
  // Smooth scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Generate schema markup for homepage
  const organizationSchema = createOrganizationSchema(
    'LightsHunt',
    window.location.origin,
    `${window.location.origin}/icons/apple-touch-icon.png`,
    'Discover and share beautiful holiday light displays in your neighborhood. Find the best Christmas lights, Halloween decorations, and holiday displays near you with reviews, schedules, and directions.',
    {
      addressCountry: 'US'
    }
  );

  const searchActionSchema = createSearchActionSchema(
    `${window.location.origin}/search`,
    'q'
  );

  // SEO content
  const pageTitle = 'LightsHunt - Find Holiday Light Displays Near You';
  const metaDescription = 'Discover amazing holiday light displays, Christmas decorations, and synchronized light shows in your neighborhood. Browse displays, read reviews, get directions, and share your own holiday lights with the community.';
  const keywords = 'holiday lights, Christmas lights, Halloween decorations, light displays, holiday decorations, Christmas displays, synchronized lights, holiday light shows, neighborhood lights, festive decorations';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:site_name" content="LightsHunt" />
        <meta property="og:image" content={`${window.location.origin}/icons/apple-touch-icon.png`} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${window.location.origin}/icons/apple-touch-icon.png`} />
        
        {/* Additional SEO meta tags */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="LightsHunt" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.origin} />
        
        {/* Preconnect to external domains for performance */}
        <link rel="preconnect" href="https://images.unsplash.com" />
        <link rel="dns-prefetch" href="https://images.unsplash.com" />
      </Helmet>

      {/* Schema Markup */}
      <SchemaMarkup schema={organizationSchema} />
      <SchemaMarkup schema={searchActionSchema} />

    <div className="min-h-screen flex flex-col">
      <NavBar />
      
      <main className="grow">
        <Hero />
        <FeaturedDisplays />
        <PopularSequences />
        <HowItWorks />
        <CTASection />
      </main>
      
      <Footer />
    </div>
    </>
  );
};

export default Index;
